import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, DollarSign, TrendingUp, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LabelList, BarChart, Bar } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPENSE_TYPES = [
  { value: 'mortgage_principal_interest', label: 'Mortgage Principal and Interest' },
  { value: 'hoa', label: 'HOA Fees' },
  { value: 'lawn_care', label: 'Lawn Care' },
  { value: 'property_management', label: 'Property Management' },
  { value: 'property_tax', label: 'Property Tax' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'maintenance', label: 'Maintenance Reserve' },
  { value: 'custom', label: 'Custom Expense' }
];

const COLORS = ['#1e40af', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#6366f1'];

export default function PropertyDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('financials');
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [newExpense, setNewExpense] = useState({
    expense_type: 'hoa',
    custom_name: '',
    amount: '',
    is_monthly: true
  });

  useEffect(() => {
    fetchPropertyDetails();
  }, [id]);

  useEffect(() => {
    if (property) {
      fetchExpenses();
      fetchDocuments();
      fetchFinancials();
    }
  }, [property]);

  const fetchPropertyDetails = async () => {
    try {
      const token = localStorage.getItem('portfolio_token');
      const response = await axios.get(`${API}/portfolio/properties/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Failed to load property details');
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/portfolio');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const token = localStorage.getItem('portfolio_token');
      const response = await axios.get(`${API}/portfolio/properties/${id}/expenses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchDocuments = async () => {
    // Get documents from the property object if they exist
    const propertyDocs = property?.documents || [];
    if (propertyDocs.length === 0) {
      setDocuments([]);
      return;
    }

    try {
      const token = localStorage.getItem('portfolio_token');
      // Send document paths to the backend to get signed URLs
      const response = await axios.post(`${API}/chat/documents/generate-signed-urls`, 
        propertyDocs, 
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchFinancials = async () => {
    try {
      const token = localStorage.getItem('portfolio_token');
      const response = await axios.get(`${API}/portfolio/properties/${id}/financials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('=== FINANCIALS API RESPONSE ===');
      console.log('Full financials data:', response.data);
      
      // Ensure we're working with the data object
      const data = response.data || {};
      
      // Use total_cash_invested if available, otherwise fall back to calculated value
      if (data.total_cash_invested && data.total_cash_invested > 0) {
        console.log(`Using total_cash_invested from API: $${data.total_cash_invested.toLocaleString()}`);
        data.total_investment = data.total_cash_invested;
      } else {
        console.log('Calculating total_investment from purchasePrice and closingCosts');
        // Fallback calculation if total_cash_invested is not available
        const purchasePrice = parseFloat(data.purchasePrice) || 0;
        const closingCosts = parseFloat(data.closingCosts) || 0;
        const calculatedInvestment = purchasePrice + closingCosts;
        
        console.log(`Purchase Price: $${purchasePrice.toLocaleString()}`);
        console.log(`Closing Costs: $${closingCosts.toLocaleString()}`);
        console.log(`Calculated Total Investment: $${calculatedInvestment.toLocaleString()}`);
        
        data.total_investment = calculatedInvestment > 0 ? calculatedInvestment : 0;
      }
      
      setFinancials(data);
    } catch (error) {
      console.error('Error fetching financials:', error);
      toast.error('Failed to load financial data');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('portfolio_token');
      await axios.post(
        `${API}/portfolio/properties/${id}/expenses`,
        {
          ...newExpense,
          amount: parseFloat(newExpense.amount)
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success('Expense added successfully!');
      setNewExpense({
        expense_type: 'hoa',
        custom_name: '',
        amount: '',
        is_monthly: true
      });
      fetchExpenses();
      fetchFinancials(); // Refresh financials
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      const token = localStorage.getItem('portfolio_token');
      await axios.delete(`${API}/portfolio/properties/${id}/expenses/${expenseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Expense deleted successfully!');
      fetchExpenses();
      fetchFinancials(); // Refresh financials
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  // Prepare chart data (safe defaults)
  const cashFlowData = useMemo(() => {
    if (!financials) return [];
    return [
      { name: 'Monthly Rent', value: financials.monthly_rent || 0 },
      { name: 'Total Expenses', value: -(financials.monthly_mortgage_payment || 0) - (financials.monthly_expenses || 0) },
      { name: 'Net Cash Flow', value: financials.monthly_net_cash_flow || 0 },
    ];
  }, [financials]);

  const expenseBreakdown = useMemo(() => {
    return financials?.expense_breakdown || [];
  }, [financials]);

  // Helper function (not a hook, safe anywhere)
  const getExpenseLabel = (expenseType, customName = '') => {
    if (expenseType === 'custom' && customName) return customName;
    const expenseTypeObj = EXPENSE_TYPES.find((t) => t.value === expenseType);
    return expenseTypeObj
      ? expenseTypeObj.label
      : expenseType.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formattedExpenseBreakdown = useMemo(() => {
    const breakdown = expenseBreakdown.map((expense) => ({
      ...expense,
      type: getExpenseLabel(expense.type, expense.name),
    }));

    const totalAmount = breakdown.reduce((sum, expense) => sum + (expense.amount || 0), 0);

    const result = breakdown.map((expense) => ({
      ...expense,
      percent: totalAmount > 0 ? (expense.amount || 0) / totalAmount : 0,
    }));

    // Sort expenses in descending order by amount for pie chart
    return result.sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }, [expenseBreakdown]);

  const allExpenses = useMemo(() => {
    const expensesList = [...expenses];

    if (financials && (financials.monthly_mortgage_payment || 0) > 0) {
      const existingMortgageExpense = expenses.find(
        (e) => e.expense_type === 'mortgage_principal_interest'
      );

      if (!existingMortgageExpense) {
        expensesList.push({
          id: 'calculated-mortgage',
          expense_type: 'mortgage_principal_interest',
          custom_name: '',
          amount: financials.monthly_mortgage_payment || 0,
          is_monthly: true,
          is_calculated: true,
        });
      }
    }

    // Sort expenses in descending order by amount
    return expensesList.sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }, [expenses, financials]);

  // Calculate total expenses
  const totalExpenses = useMemo(() => {
    return allExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
  }, [allExpenses]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
          <p className="mt-4 text-slate-600">Loading property...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-xl font-semibold text-slate-700 mb-4">Property Not Found</h3>
            <Button onClick={() => navigate('/portfolio')}>
              Back to Portfolio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Fixed Header with Back Button - Adjusted to account for navbar */}
        <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-40 py-2 px-4 border-b border-slate-200">
          <div className="max-w-7xl mx-auto flex items-center h-12 gap-2">
            <Button
              onClick={() => navigate('/portfolio')}
              variant="ghost"
              size="sm"
              className="group flex items-center gap-1.5 px-3 py-2 h-9 text-sm font-medium text-slate-700 hover:text-[#1e40af] hover:bg-slate-100/80 rounded-lg transition-colors duration-200"
            >
              <ArrowLeft className="h-4 w-4 text-[#1e40af] group-hover:-translate-x-0.5 transition-transform duration-200" />
              <span className="hidden sm:inline-block">Back to Portfolio</span>
              <span className="inline-block sm:hidden">Back</span>
            </Button>
            <div className="text-sm font-medium text-slate-800 overflow-hidden">
              <div className="truncate">{property?.address}</div>
            </div>
          </div>
        </div>
        
        {/* Main Content - Added padding to account for fixed header */}
        <div className="pt-20 px-4">
          <h1 className="text-2xl font-bold text-[#1e3a8a] mt-2">{property.address}</h1>
          <p className="text-slate-600 mt-1">{property.property_type} • {property.bedrooms} bd / {property.bathrooms} ba</p>
        </div>

        {/* Summary Cards */}
        {financials && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card className="border-l-2 md:border-l-4 border-[#10b981] sm:col-span-1">
              <CardContent className="p-3 sm:p-4 md:pt-6">
                <div className="text-xs sm:text-sm font-medium text-slate-500 mb-1">Monthly Cash Flow</div>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#10b981] whitespace-nowrap">
                  ${financials.monthly_net_cash_flow?.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-2 md:border-l-4 border-[#3b82f6] sm:col-span-1">
              <CardContent className="p-3 sm:p-4 md:pt-6">
                <div className="text-xs sm:text-sm font-medium text-slate-500 mb-1">ROI</div>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#3b82f6]">
                  {financials.roi?.toFixed(1)}%
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-2 md:border-l-4 border-[#8b5cf6] sm:col-span-1">
              <CardContent className="p-3 sm:p-4 md:pt-6">
                <div className="text-xs sm:text-sm font-medium text-slate-500 mb-1">Cash-on-Cash</div>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#8b5cf6]">
                  {financials.cash_on_cash_return?.toFixed(1)}%
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-2 md:border-l-4 border-[#f59e0b] sm:col-span-1">
              <CardContent className="p-3 sm:p-4 md:pt-6">
                <div className="text-xs sm:text-sm font-medium text-slate-500 mb-1">Total Investment</div>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#f59e0b] whitespace-nowrap">
                  ${financials?.total_investment ? financials.total_investment.toLocaleString() : '0'}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="relative mb-6">
          <div className="flex overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
            <div className="flex space-x-1 min-w-max">
              <button
                onClick={() => setActiveTab('financials')}
                className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'financials'
                    ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a] bg-blue-50/50'
                    : 'text-slate-600 hover:text-[#1e3a8a] hover:bg-slate-100/50 rounded-t-md'
                }`}
              >
                Financials
              </button>
              <button
                onClick={() => setActiveTab('expenses')}
                className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'expenses'
                    ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a] bg-blue-50/50'
                    : 'text-slate-600 hover:text-[#1e3a8a] hover:bg-slate-100/50 rounded-t-md'
                }`}
              >
                Expenses
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'documents'
                    ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a] bg-blue-50/50'
                    : 'text-slate-600 hover:text-[#1e3a8a] hover:bg-slate-100/50 rounded-t-md'
                }`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'performance'
                    ? 'text-[#1e3a8a] border-b-2 border-[#1e3a8a] bg-blue-50/50'
                    : 'text-slate-600 hover:text-[#1e3a8a] hover:bg-slate-100/50 rounded-t-md'
                }`}
              >
                Performance
              </button>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-200 -z-10"></div>
        </div>

        {/* Tab Content */}
        {activeTab === 'financials' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Purchase Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Purchase Price</span>
                  <span className="font-semibold">${(property.purchase_price || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Purchase Date</span>
                  <span className="font-semibold">{property.purchase_date ? new Date(property.purchase_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Down Payment</span>
                  <span className="font-semibold">${(property.down_payment || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Mortgage Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Principal</span>
                  <span className="font-semibold">${(property.mortgage_principal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Interest Rate</span>
                  <span className="font-semibold">{property.interest_rate || 0}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Term</span>
                  <span className="font-semibold">{property.mortgage_term || 0} months</span>
                </div>
                {financials && (
                  <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="text-slate-600 font-medium">Monthly Payment</span>
                    <span className="font-bold text-[#1e3a8a]">${(financials.monthly_mortgage_payment || 0).toLocaleString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {financials && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1e3a8a]">Monthly Cash Flow</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Rental Income</span>
                      <span className="font-semibold text-[#10b981]">+${(financials.monthly_rent || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Mortgage Payment</span>
                      <span className="font-semibold text-red-600">-${(financials.monthly_mortgage_payment || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total Expenses</span>
                      <span className="font-semibold text-red-600">-${(financials.monthly_expenses || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t-2 border-slate-300">
                      <span className="text-slate-700 font-bold">Net Cash Flow</span>
                      <span className={`font-bold text-lg ${(financials.monthly_net_cash_flow || 0) >= 0 ? 'text-[#10b981]' : 'text-red-600'}`}>
                        ${Math.abs(financials.monthly_net_cash_flow || 0).toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#1e3a8a]">Annual Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Annual Rent</span>
                      <span className="font-semibold">${(financials.annual_rent || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Annual Mortgage</span>
                      <span className="font-semibold">${(financials.annual_mortgage || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Annual Expenses</span>
                      <span className="font-semibold">${(financials.annual_expenses || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t-2 border-slate-300">
                      <span className="text-slate-700 font-bold">Annual Cash Flow</span>
                      <span className={`font-bold text-lg ${(financials.annual_net_cash_flow || 0) >= 0 ? 'text-[#10b981]' : 'text-red-600'}`}>
                        ${Math.abs(financials.annual_net_cash_flow || 0).toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Add Expense Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Add/Update Expense</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Mortgage warning */}
                {financials && financials.monthly_mortgage_payment > 0 && !expenses.find(e => e.expense_type === 'mortgage_principal_interest') && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> Mortgage Principal & Interest is automatically calculated from your mortgage details and shown below.
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleAddExpense} className="space-y-4">
                  <div>
                    <Label htmlFor="expense_type">Expense Type</Label>
                    <select
                      id="expense_type"
                      value={newExpense.expense_type}
                      onChange={(e) => setNewExpense({ ...newExpense, expense_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]"
                    >
                      {EXPENSE_TYPES.filter(type => {
                        // Hide mortgage option if calculated mortgage exists
                        const hasCalculatedMortgage = financials && financials.monthly_mortgage_payment > 0;
                        const existingMortgageExpense = expenses.find(e => e.expense_type === 'mortgage_principal_interest');
                        if (type.value === 'mortgage_principal_interest' && hasCalculatedMortgage && !existingMortgageExpense) {
                          return false;
                        }
                        return true;
                      }).map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {newExpense.expense_type === 'custom' && (
                    <div>
                      <Label htmlFor="custom_name">Custom Name</Label>
                      <Input
                        id="custom_name"
                        value={newExpense.custom_name}
                        onChange={(e) => setNewExpense({ ...newExpense, custom_name: e.target.value })}
                        placeholder="Enter expense name"
                        required={newExpense.expense_type === 'custom'}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="amount">Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                      placeholder="250.00"
                      required
                      min="0"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_monthly"
                      checked={newExpense.is_monthly}
                      onChange={(e) => setNewExpense({ ...newExpense, is_monthly: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_monthly" className="cursor-pointer">Monthly expense</Label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save Expense
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Expense List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Current Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                {allExpenses.length === 0 ? (
                  <p className="text-center text-slate-600 py-8">No expenses added yet</p>
                ) : (
                  <div className="space-y-3">
                    {allExpenses.map((expense) => (
                      <div
                        key={expense.id}
                        className={`flex justify-between items-center p-3 rounded-lg ${
                          expense.is_calculated ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'
                        }`}
                      >
                        <div>
                          <p className="font-semibold text-slate-900">
                            {expense.expense_type === 'custom' 
                              ? expense.custom_name 
                              : EXPENSE_TYPES.find(t => t.value === expense.expense_type)?.label
                            }
                            {expense.is_calculated && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                Calculated
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-slate-600">
                            {expense.is_monthly ? 'Monthly' : 'One-time'}
                            {expense.is_calculated && (
                              <span className="ml-2 text-xs text-blue-600">
                                (From mortgage details)
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-bold text-[#1e3a8a]">
                            ${(expense.amount || 0).toLocaleString()}
                          </span>
                          {!expense.is_calculated && (
                            <Button
                              onClick={() => handleDeleteExpense(expense.id)}
                              variant="outline"
                              size="sm"
                              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Total Expenses */}
                {allExpenses.length > 0 && (
                  <div className="mt-6 pt-4 border-t-2 border-slate-300">
                    <div className="flex justify-between items-center">
                      <span className="text-base sm:text-lg font-bold text-slate-700">Total Monthly Expenses</span>
                      <span className="text-lg sm:text-xl font-bold text-[#1e3a8a]">
                        ${totalExpenses.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'documents' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1e3a8a]">Property Documents</CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-center text-slate-600 py-8">No documents found for this property.</p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex justify-between items-center p-3 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <p className="font-semibold text-slate-900">
                          {doc.filename}
                        </p>
                        <p className="text-sm text-slate-600">
                          Type: {doc.type || 'N/A'} • Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-3">
                        {doc.view_url ? (
                          <Button
                            onClick={() => window.open(doc.view_url, '_blank')}
                            variant="outline"
                            size="sm"
                            className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                          >
                            View
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" disabled>
                            Unavailable
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'performance' && financials && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Monthly Cash Flow Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={cashFlowData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#1e3a8a" strokeWidth={2}>
                      <LabelList dataKey="value" position="top" formatter={(value) => 
                        typeof value === 'number' ? `$${Math.abs(value).toLocaleString()}` : value
                      } />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {formattedExpenseBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">Expense Distribution</CardTitle>
                </CardHeader>
                <CardContent>
<ResponsiveContainer width="100%" height={280}>
  <PieChart>
    <Pie
      data={formattedExpenseBreakdown}
      cx="50%"
      cy="50%"
      labelLine={false}
      outerRadius={80}
      fill="#8884d8"
      dataKey="amount"
      nameKey="type"
      label={{ fontSize: 10, formatter: ({ name, percent }) => {
        const percentage = percent ? (percent * 100).toFixed(0) : '0';
        return `${name} (${percentage}%)`;
      }}}
    >
      {formattedExpenseBreakdown.map((entry, index) => (
        <Cell 
          key={`cell-${index}`} 
          fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#4f46e5', '#f59e0b', '#8884d8'][index % 7]} 
        />
      ))}
    </Pie>
    <Tooltip 
      formatter={(value, name, props) => {
        const percent = props.payload.percent ? (props.payload.percent * 100).toFixed(1) : '0.0';
        return [`$${Number(value).toLocaleString()} (${percent}%)`, props.payload.type || name];
      }}
    />
    <Legend 
      layout="horizontal" 
      verticalAlign="bottom" 
      align="center"
      formatter={(value, entry, index) => {
        const item = formattedExpenseBreakdown[index];
        return <span className="text-xs sm:text-sm">{item?.type || value}</span>;
      }}
    />
  </PieChart>
</ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Cash Flow Bar Chart */}
            {cashFlowData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">Cash Flow Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={cashFlowData} margin={{ top: 40, right: 10, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        hide={true}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Amount']}
                        labelFormatter={(label) => label}
                      />
                      <Legend />
                      <Bar 
                        dataKey="value" 
                        name="Amount"
                        fill={(data) => data.value >= 0 ? '#10b981' : '#ef4444'}
                        label={{ position: 'top', fontSize: 11, formatter: (value, index) => cashFlowData[index]?.name || '' }}
                      >
                        {cashFlowData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.value >= 0 ? '#10b981' : '#ef4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 text-center text-xs sm:text-sm text-slate-600">
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
                      <span className="inline-flex items-center">
                        <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                        Positive Cash Flow (Income)
                      </span>
                      <span className="inline-flex items-center">
                        <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                        Negative Cash Flow (Expenses)
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">Key Performance Indicators</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gradient-to-r from-[#10b981]/10 to-[#10b981]/5 rounded-lg">
                    <span className="text-slate-700 font-medium">ROI</span>
                    <span className="text-2xl font-bold text-[#10b981]">{financials.roi.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gradient-to-r from-[#1e3a8a]/10 to-[#1e3a8a]/5 rounded-lg">
                    <span className="text-slate-700 font-medium">Cash-on-Cash Return</span>
                    <span className="text-2xl font-bold text-[#1e3a8a]">{financials.cash_on_cash_return ? financials.cash_on_cash_return.toFixed(2) : '0.00'}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gradient-to-r from-[#059669]/10 to-[#059669]/5 rounded-lg">
                    <span className="text-slate-700 font-medium">Annual Cash Flow</span>
                    <span className={`text-2xl font-bold ${(financials.annual_net_cash_flow || 0) >= 0 ? 'text-[#059669]' : 'text-red-600'}`}>
                      ${Math.abs(financials.annual_net_cash_flow || 0).toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">Investment Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Cash Invested</span>
                    <span className="font-semibold">${(financials.total_cash_invested || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Annual Return</span>
                    <span className="font-semibold text-[#10b981]">${(financials.annual_net_cash_flow || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="text-slate-700 font-medium">Payback Period</span>
                    <span className="font-semibold">
                      {(financials.annual_net_cash_flow || 0) > 0
                        ? `${((financials.total_cash_invested || 0) / financials.annual_net_cash_flow).toFixed(1)} years`
                        : 'N/A'
                      }
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
